import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import Button from 'portal-modules/common/components/Button'
import RpModal from './rp-modal'

import styled from '@emotion/styled'
import { css } from '@emotion/core'
import DangerousInnerHTML from 'portal-modules/common/components/DangerousInnerHTML'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'

const StyledSpan = styled('span')`
  font-size: 14px;
`
const StyledLi = styled('li')`
  font-size: 14px;
  margin-bottom: 8px;
`
const StyledAutoPaymentDetails = styled('div')`
  max-height: 280px;
  overflow: auto;
`

const btnStyle = css`
  span > {
    text-decoration: none;
  }
`

const customStyledSpan = css`
  display: block;
  padding-bottom: 12px;
`

const StyledButtonWrapper = styled('div')`
  display: flex;
  justify-content: center;
  > button {
    margin-right: 0;
    
    /* Fix for responsive text wrapping on smaller screens */
    @media only screen and (max-width: 768px) {
      white-space: normal !important;
      word-wrap: break-word;
      min-height: 64px;
      height: auto;
      padding: 16px 20px;
      line-height: 1.3;
      font-size: 14px;
    }
    
    @media only screen and (max-width: 480px) {
      font-size: 13px;
      padding: 12px 16px;
    }
  }
`

const CustomButton = ({ text, appearance, onClick }) => (
  <StyledButtonWrapper>
      <Button 
        appearance={appearance} 
        onClick={onClick}
      size='large'
      autoHeight={true}
    >
      {text}
    </Button>
  </StyledButtonWrapper>
)

CustomButton.propTypes = {
  text: PropTypes.string.isRequired,
  appearance: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
}

const getEndDate = end_date => {
  let text = ''
  switch (end_date) {
    case 0:
      text = __('that ends <strong>when I cancel.</strong>')
      break
    case 1:
      text = __('that ends when <strong>I cancel or move out.</strong>')
      break
    default:
      text = __('that ends <strong>{%t, 0, MMM, YYYY}.</strong>', [
        new Date(end_date * 1000),
      ])
      break
  }

  return text
}

const getContents = (payment, commonText, multiPayments = false) => {
  const Component = multiPayments ? StyledLi : StyledSpan
  const {
    payment_amount,
    type,
    start_date,
    end_date,
    first_payment,
    second_payment,
    id,
  } = payment

  const endDateText = getEndDate(end_date)

  switch (type) {
    case 'bimonthly':
      return (
        <Component key={id}>
          <DangerousInnerHTML key={id}>
            {multiPayments
              ? __(
                  'Payment of <strong>{%m, 0}</strong> amount on the <strong>{%t, 1, Do}</strong>, and payment of <strong>{%m, 2}</strong> amount on the <strong>{%t, 3, Do}</strong> of each month </strong>{%s, 4} {%s, 5}',
                  [
                    first_payment.max_payment_amount,
                    first_payment.start_date,
                    second_payment.max_payment_amount,
                    second_payment.start_date,
                    endDateText,
                    commonText,
                  ],
                )
              : __(
                  'This payment method was assigned to a <strong>{%m, 0}</strong> amount, and <strong>{%m, 1}</strong> amount auto payment and was scheduled to be charged from the <strong>{%t, 2, Do}</strong>, and <strong>{%t, 3, Do} of each month </strong>{%s, 4} {%s, 5}',
                  [
                    first_payment.max_payment_amount,
                    second_payment.max_payment_amount,
                    first_payment.start_date,
                    second_payment.start_date,
                    endDateText,
                    commonText,
                  ],
                )}
          </DangerousInnerHTML>
        </Component>
      )
    default:
      return (
        <Component key={id}>
          <DangerousInnerHTML key={id}>
            {multiPayments
              ? payment_amount > 0
                ? __(
                    'Payment of <strong>{%m, 0}</strong> amount on the <strong>{%t, 1, Do} of each month </strong> {%s, 2}',
                    [payment_amount, start_date, endDateText],
                  )
                : __(
                    'Payment of variable amount on the <strong>{%t, 0, Do} of each month </strong>{%s, 1}',
                    [start_date, endDateText],
                  )
              : payment_amount > 0
              ? __(
                  'This payment method was assigned to a <strong>{%m, 0}</strong> amount auto payment and was scheduled to be charged from the <strong>{%t, 1, Do} of each month </strong> {%s, 2} {%s, 3}',
                  [payment_amount, start_date, endDateText, commonText],
                )
              : __(
                  'This payment method was assigned to a variable amount auto payment and was scheduled to be charged from the <strong>{%t, 0, Do} of each month </strong>{%s, 1} {%s, 2}',
                  [start_date, endDateText, commonText],
                )}
          </DangerousInnerHTML>
        </Component>
      )
  }
}

let InformationModal = ({
  title,
  isSepa,
  handlePaymentDelete,
  handleAutoPaymentEdit,
  connectedAutoPayments,
  hasRefundAccount,
  handleRefundAccountEdit,
  addPaymentMethodAccess,
  isAddAutoPaymentAllowed = false,
  isEditAutoPaymentAllowed = false,
  isDeleteAutoPaymentAllowed = false,
  ...rest
}) => {
  const { isRP } = getProjectConditions()
  isAddAutoPaymentAllowed = isRP ? isAddAutoPaymentAllowed : true
  isEditAutoPaymentAllowed = isRP ? isEditAutoPaymentAllowed : true
  isDeleteAutoPaymentAllowed = isRP ? isDeleteAutoPaymentAllowed : true
  const paymentText =
    connectedAutoPayments.length > 1 ? __('auto-payments') : __('auto-payment')
  const refundText = __(
    'This payment account is being used as your preferred refund method. Removing this account will also remove your preferred refund method.',
  )
  const commonText = isSepa
    ? __('Would you like to assign a new payment method?')
    : connectedAutoPayments.length &&
      connectedAutoPayments.length == 1 &&
      hasRefundAccount
    ? __(
        '<br /><br />{%s, 0}<br /><br /> Would you like to assign a new payment method or would you like to delete your {%s, 1} and refund method?',
        [refundText, paymentText],
      )
    : connectedAutoPayments.length &&
      connectedAutoPayments.length > 1 &&
      hasRefundAccount
    ? __(
        'Would you like to assign a new payment method or would you like to delete your {%s, 0} and refund method?',
        [paymentText],
      )
    : !connectedAutoPayments.length && hasRefundAccount
    ? __(
        '{%s, 0} Would you like to update your preferred refund method or would you like to delete it?',
        [refundText],
      )
    : connectedAutoPayments.length &&
      connectedAutoPayments.length > 1 &&
      !hasRefundAccount &&
      isDeleteAutoPaymentAllowed &&
      !isEditAutoPaymentAllowed
    ? __(
        'Removing this payment method will also delete your auto-payments. Would you like to delete your {%s, 0}?',
        [paymentText],
      )
    : connectedAutoPayments.length &&
      !hasRefundAccount &&
      isDeleteAutoPaymentAllowed &&
      !isEditAutoPaymentAllowed
    ? __(
        'Removing this payment method will also delete your auto-payment. Would you like to delete your {%s, 0}?',
        [paymentText],
      )
    : connectedAutoPayments.length &&
      connectedAutoPayments.length > 1 &&
      !hasRefundAccount &&
      !isDeleteAutoPaymentAllowed &&
      isEditAutoPaymentAllowed
    ? __(
        'To remove this payment method you will need to update your auto payments to use a new payment accounts.',
      )
    : connectedAutoPayments.length &&
      !hasRefundAccount &&
      !isDeleteAutoPaymentAllowed &&
      isEditAutoPaymentAllowed
    ? __(
        'To remove this payment method you will need to update your auto payment to use a new payment account.',
      )
    : connectedAutoPayments.length &&
      connectedAutoPayments.length == 1 &&
      !hasRefundAccount &&
      !isDeleteAutoPaymentAllowed &&
      !isEditAutoPaymentAllowed
    ? __(
        "This payment method can't be removed until the auto payment is removed. Please contact your property for assistance with deleting this payment method.",
      )
    : connectedAutoPayments.length &&
      !hasRefundAccount &&
      !isDeleteAutoPaymentAllowed &&
      !isEditAutoPaymentAllowed
    ? __(
        "This payment method can't be removed until the auto payments are removed. Please contact your property for assistance with deleting this payment method.",
      )
    : connectedAutoPayments.length &&
      connectedAutoPayments.length == 1 &&
      isDeleteAutoPaymentAllowed &&
      isEditAutoPaymentAllowed
    ? __(
        'Would you like to assign a new payment method or would you like to delete your auto-payment?',
      )
    : connectedAutoPayments.length &&
      connectedAutoPayments.length > 1 &&
      isDeleteAutoPaymentAllowed &&
      isEditAutoPaymentAllowed
    ? __(
        'Would you like to assign a new payment method or would you like to delete your auto-payments?',
      )
    : ''
  return (
    <RpModal
      title={title}
      {...rest}
      footerContent={
        <Fragment>
          {!isSepa &&
          connectedAutoPayments.length &&
          isEditAutoPaymentAllowed ? (
            <CustomButton
              text={__('Update Auto Payment(s)')}
              appearance='primary'
              onClick={handleAutoPaymentEdit}
            />
          ) : (
            !hasRefundAccount &&
            isDeleteAutoPaymentAllowed && (
              <CustomButton
                text={__('Keep Auto-Payment(s) and Payment Method')}
                appearance='primary'
                onClick={rest.onClose}
              />
            )
          )}
          {!!hasRefundAccount && addPaymentMethodAccess && (
            <CustomButton
              text={__('Update Refund Method')}
              appearance='primary'
              onClick={handleRefundAccountEdit}
            />
          )}
          {connectedAutoPayments.length && isDeleteAutoPaymentAllowed ? (
            <CustomButton
              text={
                connectedAutoPayments.length &&
                isDeleteAutoPaymentAllowed &&
                hasRefundAccount
                  ? __('Delete Both Auto-Payment(s) and Refund Method')
                  : connectedAutoPayments.length &&
                    isDeleteAutoPaymentAllowed &&
                    !hasRefundAccount
                  ? __('Delete Auto-Payment(s)')
                  : !connectedAutoPayments.length && hasRefundAccount
                  ? __('Yes, Delete Refund Method')
                  : ''
              }
              appearance='secondary'
              onClick={handlePaymentDelete}
            />
          ) : !connectedAutoPayments.length && hasRefundAccount ? (
            <CustomButton
              text={__('Yes, Delete Refund Method')}
              appearance='secondary'
              onClick={handlePaymentDelete}
            />
          ) : (
            <CustomButton
              text={__('Cancel')}
              appearance='secondary'
              onClick={rest.onClose}
            />
          )}
        </Fragment>
      }
    >
      <StyledAutoPaymentDetails>
        {connectedAutoPayments.length == 1 ? (
          Object.values(connectedAutoPayments).map(payment =>
            getContents(payment, commonText, false),
          )
        ) : connectedAutoPayments.length > 1 ? (
          <Fragment>
            <StyledSpan>
              {__(
                'The payment method is assigned to the following auto payments:',
              )}
            </StyledSpan>
            <br />
            <ul>
              {Object.values(connectedAutoPayments).map(payment =>
                getContents(payment, '', true),
              )}
            </ul>
            {!!hasRefundAccount && (
              <StyledSpan css={customStyledSpan}>{refundText}</StyledSpan>
            )}
            <StyledSpan>{commonText}</StyledSpan>
          </Fragment>
        ) : (
          <Fragment>
            <StyledSpan>{commonText}</StyledSpan>
          </Fragment>
        )}
      </StyledAutoPaymentDetails>
    </RpModal>
  )
}

InformationModal.propTypes = {
  /** Title of the popup. */
  title: PropTypes.string.isRequired,
  /** To hide 'Use New Payment Method' button for SEPA payment type. */
  isSepa: PropTypes.bool,
  /** array of scheduled payment onject */
  connectedAutoPayments: PropTypes.array.isRequired,
  /** to handle new payment payment method button click */
  handleAutoPaymentEdit: PropTypes.func.isRequired,
  /** To delete payment account and associated scheduled payments */
  handlePaymentDelete: PropTypes.func.isRequired,
  hasRefundAccount: PropTypes.bool,
  handleRefundAccountEdit: PropTypes.func,
  isAddAutoPaymentAllowed: PropTypes.bool,
  isEditAutoPaymentAllowed: PropTypes.bool,
  isDeleteAutoPaymentAllowed: PropTypes.bool,
}

export default InformationModal
